// Session-related types extracted from useSession. Public types are
// re-exported by useSession.ts so existing consumers that import from
// '../hooks/useSession' keep working.

import type { FeatureBacklogItem } from '../utils/featureBacklogModel';
import type { ProcessGraph } from '../utils/processGraph';
import type { NewModel } from '../utils/flowModel';
import type { TurnFact } from '../utils/turnFacts';

export type { TurnFact } from '../utils/turnFacts';

// ─── Public types ──────────────────────────────────────────────────

export interface AttachmentRecord {
  filename: string;
  mime: string;
  size: number;
  // Where the bytes were filed. The discovery turn records `blobPath`, the
  // facilitation turn records `sourceRef`, and the turn the browser has just
  // sent has neither yet - it knows only what the consultant attached.
  blobPath?: string;
  sourceRef?: string;
  kind?: string;
}

export interface ConversationMessage {
  id?: string;
  type: 'user' | 'agent' | 'transcript';
  text: string;
  timestamp: string;
  // Server attaches the per-turn attachment metadata onto the user
  // message in conversation.json (see _chat2Internal.js).
  attachments?: AttachmentRecord[];
  // 093: the knowledge pages this reply drew on, lifted from the turn's done
  // frame. Present only on a facilitator reply that used the knowledge base;
  // the Sources footer under the reply renders them as links.
  citations?: ReplyCitation[];
  // 100 (owner decision 7): what this reply captured, named in plain words.
  // The facts belong to the reply that produced them, so the footer under that
  // reply lists them; they do not survive as a band above the conversation.
  facts?: TurnFact[];
}

// One page a reply used, named so the consultant can open it.
export interface ReplyCitation {
  path: string;
  title: string;
}

// ── Activity feedback (056; owner-agreed activity feedback, 2026-07-09) ──
// The view-state shape of one background activity on the current turn (a
// server advisory tool run, surfaced by the `activity` SSE frame). Ephemeral:
// held in hook state for the in-flight turn only, never persisted. `running`
// renders as the quiet spinner row, `complete` as the settled disclosure
// (with the optional outcome `detail`), `failed` renders nothing.
export interface ChatActivity {
  id: string;
  kind?: string;
  label: string;
  status: 'running' | 'complete' | 'failed';
  detail?: string;
}

export type ConversationMode = 'discovery' | 'infer' | 'watch';

export interface CreateSessionRefs {
  engagementId?: string;
  streamId?: string;
}

// ─── Narrate → Refine (002-narrate-capture) ────────────────────────
// The two-phase session lifecycle. `narrate` shows the forming spine;
// `refine` shows the flow canvas. `undefined` ⇒ a pre-feature session,
// which the renderer treats as `refine` (FR-015).
export type SessionPhase = 'narrate' | 'refine';

// The done frame can carry the Narrate→Refine session phase + spine
// alongside the newModel (002-narrate-capture). They ride on the second
// onDone callback argument as extra (optional) fields so the existing
// two-arg onDone contract is unchanged; callers that don't care simply
// ignore them. Backward-safe: a frame without phase/spine leaves both
// absent.
//
// `kind` is the per-turn classification the server attaches to the done
// frame (FR-008): a 'question' continues the interview, a 'nudge' prompts
// the SME to keep narrating, and an 'offer' signals the agent thinks the
// flow is complete and offers the move to the map. Defaults to 'question'
// when the frame omits it (pre-feature frames).
//
// Lives here (alongside SpineStep/SessionPhase, the types it composes)
// rather than in utils/chat2Stream so the type layer has no edge back
// into the transport — breaking the chat2Stream ↔ sessionTypes /
// sessionCoercers import cycle (no-circular). chat2Stream re-exports it.
export type DoneModel = NewModel & {
  surfaceActions?: SurfaceAction[];
  phase?: SessionPhase;
  spine?: SpineStep[];
  kind?: 'question' | 'nudge' | 'offer';
  // 049 screen-aware-chat — the vision turn's elicitation envelope, lifted opaquely
  // onto the model exactly as phase/spine (a discovery frame omits them). The chat
  // engine computes them server-side from the live aggregate + the declared plan.
  outstanding?: string[];
  // FR-017: the live inferred-candidate set (≤3 per target), recomputed each turn.
  candidates?: { slotId: string; member?: string; value: string; source: string; rationale?: string }[];
  // 093: the knowledge pages the turn cited, lifted from the done frame the
  // same way. A turn that used no page leaves the key absent.
  citations?: ReplyCitation[];
  // 054 facilitated-vision (FR-021/T045): the turn's appended, server-ordered
  // fact events, lifted OPAQUELY from the done frame (verbatim array copy —
  // eventId/seq are the store's ordering truth, so no per-event coercion).
  // A discovery frame never carries it, so the model is unchanged there.
  factEvents?: unknown[];
};

export interface SurfaceAction {
  schemaVersion: 1;
  actionId: string;
  sessionId: string;
  screenId: string;
  sourcePresentationRevision: string;
  type: string;
  payload: Record<string, unknown>;
}

// A single spine step — the plain-language projection shown while
// narrating. `order` is 1-based and contiguous; `state` conveys whether
// the step is settled (`unconfirmed`) or flagged to revisit (`uncertain`).
//
// Branch-aware fields (004-branch-aware-spine) grow the row so SpineView can
// assemble the nested-lane DOM from this flat array. They are all OPTIONAL and
// backward-safe: a bare `{ order, label, state }` step (a pre-004 frame, or a
// linear flow) is valid and renders exactly as before — missing `laneId` is
// treated as the trunk, missing `marker` as `'said'`, missing `needsCheck` as
// `false`. The server emits them (functions/_narrateSpine.js); the wire hop
// forwards the array opaquely (chat2Stream/useSession), so the types only
// describe the richer shape — they never gate the pass-through.
export interface SpineStep {
  order: number;
  label: string;
  state: 'unconfirmed' | 'uncertain';
  // Lane identity. `'trunk'` (or absent) ⇒ the main backbone; a distinct id
  // per branch / separate journey. Every trunk step shares the trunk id.
  laneId?: string;
  // Human lane label: `''` on the trunk, a label on a branch / separate journey.
  laneLabel?: string;
  // The FORKING step's `order` a non-trunk branch lane hangs off. KEY ABSENT
  // on the trunk (and on separate journeys).
  branchOfStepOrder?: number;
  // Unreachable-from-the-trunk work (an orphan reaction) — rendered in its own
  // labelled region, never as the next trunk row.
  isSeparateJourney?: boolean;
  // Provenance: `'said'` (plainly stated) vs `'inferred'` (synthesised). Binary;
  // the projection emits `'said'` today but the render supports both.
  // 005-calm-spine-redesign: NO LONGER RENDERED. The `inferred` provenance UI
  // was dead (the projection only ever emits `'said'`) and was removed; the
  // field stays on the wire for backward-safe pass-through (FR-001/FR-007).
  marker?: 'said' | 'inferred';
  // Deterministic honesty signal — the step needs a human check. Drives the ONE
  // consolidated uncertainty cue + the header "N to check" roll-up.
  needsCheck?: boolean;
  // Non-empty deterministic reason, present iff `needsCheck`.
  // 005-calm-spine-redesign: OFF the default glance — surfaced ON DEMAND only,
  // via the non-modal disclosure under the row (FR-003/FR-006).
  checkReason?: string;
  // Spine-level overflow signal, carried on the FIRST step so it survives
  // JSON serialization over the done frame / GET (the array property does not).
  overflow?: boolean;
}

// ─── Domain dictionary + session payload (internal but shared) ─────

export interface BoundedContextAggregate {
  Name: string;
  Commands?: unknown[];
  Queries?: unknown[];
  Reactions?: unknown[];
  Properties?: unknown[];
  Types?: unknown[];
}

export interface BoundedContext {
  BoundedContextName: string;
  Aggregates?: BoundedContextAggregate[];
}

export interface DomainDictionary {
  BoundedContexts: BoundedContext[];
}

export interface SessionData {
  id: string;
  createdAt: string;
  updatedAt: string;
  conversationHistory: ConversationMessage[];
  domainDictionary: DomainDictionary;
  memorySummary?: string;
  featureBacklog?: FeatureBacklogItem[];
  processGraph?: ProcessGraph | null;
  newModel?: NewModel;
  discoveryFeatures?: {
    features?: DiscoveryFeature[];
    extractedAt?: string | null;
    statementCountAtExtraction?: number | null;
    warnings?: string[];
  } | null;
  extractionStatus?: 'pending' | 'idle'; // legacy
  memoryStatus?: 'pending' | 'idle';
  featuresStatus?: 'pending' | 'idle';
  graphStatus?: 'pending' | 'idle';
  pendingExtractionTurns?: ConversationMessage[];
  mode?: string;
  // ── Narrate → Refine (002-narrate-capture). Top-level on the GET
  //    response; `spine` is NOT nested inside newModel. Absent on
  //    pre-feature sessions, so both are optional. ──────────────────
  phase?: SessionPhase;
  spine?: SpineStep[];
}

// ─── Discovery feature extraction (post-2026-04-28) ────────────────
// Discovery mode produces its own feature backlog from the parsed flow
// (separate from the legacy `featureBacklog` array used by infer/watch).
// The shape mirrors the server-side discoveryFeatures.json blob.

export interface DiscoveryFeatureActor {
  kind: 'human' | 'context' | 'externalSystem';
  name: string;
}

export interface DiscoveryFeatureEventRef {
  id: number;
  name: string;
  target: string | null;
}

export interface DiscoveryFeatureBullet {
  text: string;
  anchors: number[]; // statement ids referenced in the bullet
}

export interface DiscoveryFeature {
  candidateId: string;
  rootStatementId: number;
  rootKind: 'action' | 'reaction';
  name: string;
  description: string;
  boundedContext: string;
  priority: 'mvp' | 'later';
  trigger: 'user-action' | 'system-reaction' | 'external-system';
  statementIds: number[];
  touchpoints: string[];
  targets: string[];
  // ── Richer detail (post-2026-05-01) — optional so older blobs that
  //    predate the schema bump still load cleanly. ─────────────────
  initiatingActor?: DiscoveryFeatureActor | null;
  inputs?: {
    actor: DiscoveryFeatureActor | null;
    via: string[];
    target: string | null;
  };
  outputs?: DiscoveryFeatureEventRef[];
  preconditions?: DiscoveryFeatureEventRef[];
  tags?: Record<string, string | true>;
  chainSummary?: string;
  userStory?: string;
  acceptanceCriteria?: DiscoveryFeatureBullet[];
  openQuestions?: DiscoveryFeatureBullet[];
  // ── Delivery view (post-2026-05-11) — marks features that came in
  //    via CSV import rather than flow extraction. Optional so old
  //    blobs load cleanly. Defaults to 'extracted' when absent.
  source?: 'extracted' | 'csv';
  // ── Richer import metadata (post-2026-05-12). Comes in via CSV
  //    columns when the scope sheet has them. All optional; absent
  //    on Discovery-extracted features.
  scopeType?: 'bc' | 'aggregate' | 'feature';
  stream?: string;                       // top-level grouping above the bounded context
  specPath?: string;                     // canonical spec location on disk (e.g. specs/x/_bc.md)
  specConfidence?: 'high' | 'med' | 'low' | 'n/a';
  justification?: string;                // free-text rationale on complexity / scope
}

export interface DiscoveryFeaturesPayload {
  features: DiscoveryFeature[];
  extractedAt: string | null;
  statementCountAtExtraction: number | null;
  warnings: string[];
  status: 'idle' | 'pending' | 'error';
}

// ─── Code-comparison (post-2026-04-30) ─────────────────────────────
// Server-side comparison of discovery features against a public GitHub
// repo. Mirrors the wire shape persisted to code_comparison.json.

export interface CodeEvidence {
  filePath: string;
  lineRange: [number, number];
  why: string;
  snippet: string;
}

export interface CodeComparisonFeatureResult {
  candidateId: string;
  rootStatementId?: number;
  featureName: string;
  status: 'found' | 'partial' | 'missing';
  evidence: CodeEvidence[];
  gapNotes: string;
}

export interface CodeComparisonSummary {
  found: number;
  partial: number;
  missing: number;
  total: number;
  fileCount?: number;
  lineCount?: number;
  languageCounts?: Record<string, number>;
  embedCacheHit?: boolean;
}

export interface CodeComparisonPayload {
  repoUrl: string;
  owner?: string;
  repo?: string;
  defaultBranch?: string;
  commitSha?: string;
  scannedAt: string;
  summary?: CodeComparisonSummary;
  features?: CodeComparisonFeatureResult[];
  warnings?: string[];
  durationMs?: number;
  // Set when the worker (or inline run) failed; the panel renders the
  // error in place of the results.
  error?: string;
}

export interface CodeComparisonState {
  comparison: CodeComparisonPayload | null;
  status: 'idle' | 'pending' | 'error';
}

// ─── Delivery pipeline (post-2026-05-11, refocused) ───────────────────
// The Delivery view holds Migration Units (MUs) — release-shaped buckets
// of features. effort / value / tags are OPTIONAL per-feature metadata,
// supplied via CSV import. AI grouping is opt-in (wizard or explicit
// re-group action), never automatic.

export type EffortPoints = 1 | 2 | 3 | 5 | 8 | 13 | 21;
export type BusinessValue = 'high' | 'med' | 'low';
export const FIBONACCI_EFFORTS: EffortPoints[] = [1, 2, 3, 5, 8, 13, 21];
export const VALUE_LEVELS: BusinessValue[] = ['high', 'med', 'low'];

export const UNASSIGNED_MU = 'Unassigned';

export interface MigrationUnit {
  name: string;
  description: string;
  comments: string;
  dependsOn: string[];
  featureIds: string[];
  flowText: string;
  flowGeneratedAt: string | null;
}

export interface FeatureScoring {
  effort?: EffortPoints;
  value?: BusinessValue;
  tags?: string[];
}

export type ScoringField = 'effort' | 'value' | 'tags';

export interface DeliveryAnalysisPayload {
  importedFeatures: DiscoveryFeature[];
  migrationUnits: MigrationUnit[];
  featureScoring: Record<string, FeatureScoring>;
  scoringManualFields: Record<string, ScoringField[]>;
  scoringGeneratedAt: string | null;
  hidden: Record<string, true>;
  basedOnExtractedAt: string | null;
  basedOnImportHash: string | null;
  warnings: string[];
  generatedAt: string | null;
  status: 'idle' | 'pending' | 'error';
}

export type MuMode = 'from-column' | 'ai-group' | 'single-mu' | 'unassigned';

export interface ImportCsvResult {
  imported: number;
  duplicates: Array<{ candidateId: string; name: string }>;
  rejected: Array<{ row: unknown; reason: string }>;
  payload: DeliveryAnalysisPayload;
}

export type DeliveryMutation =
  | { type: 'moveFeature'; featureId: string; toMu: string; index?: number }
  | { type: 'reorderMus'; names: string[] }
  | { type: 'reorderInMu'; muName: string; featureIds: string[] }
  | { type: 'renameMu'; from: string; to: string }
  | { type: 'createMu'; name: string; description?: string; featureIds?: string[] }
  | { type: 'deleteMu'; name: string }
  | { type: 'updateMuDescription'; name: string; description: string }
  | { type: 'updateMuComments'; name: string; comments: string }
  | { type: 'setMuDependsOn'; name: string; dependsOn: string[] }
  | { type: 'toggleHidden'; featureId: string; hidden: boolean }
  | { type: 'setFeatureScoring'; featureId: string; field: 'effort'; value: EffortPoints }
  | { type: 'setFeatureScoring'; featureId: string; field: 'value'; value: BusinessValue }
  | { type: 'setFeatureScoring'; featureId: string; field: 'tags'; value: string[] }
  | { type: 'clearFeatureScoring'; featureId: string; field: ScoringField }
  | { type: 'setMuFlowText'; name: string; flowText: string };

// ─── Feature specs (Phase 2) ───────────────────────────────────────
// Per-feature markdown spec, AI-drafted from the feature + MU context.
// Persisted under feature_specs.json on the session.

export interface FeatureSpec {
  featureId: string;
  markdown: string;
  generatedAt?: string;
  generatedFromFlowAt?: string | null;
  userEdited?: boolean;
  savedAt?: string;
  warnings?: string[];
  model?: string;
}

export interface FeatureSpecsPayload {
  specs: Record<string, FeatureSpec>;
}

/**
 * The complete UI-facing contract for the shared chat panel.
 *
 * Discovery, VSM and facilitation may own additional workflow state, but the
 * panel receives only this controller.
 */
export interface ChatController {
  conversationHistory: ConversationMessage[];
  isLoading: boolean;
  inputBusy: boolean;
  callStatus: string;
  handleTextMessage: (message: string, files?: File[]) => Promise<void>;
  startVoiceCall: () => Promise<void>;
  endVoiceCall: () => void;
  importFromDocument: (
    documentText: string,
    opts?: { filename?: string; force?: boolean },
  ) => Promise<DocImportResult>;
  onEscape?: () => void;
  sessionError: string | null;
  activities?: ChatActivity[];
}

// ─── Hook surface ──────────────────────────────────────────────────

export interface UseSessionReturn {
  // The one neutral controller consumed by SessionCapturePanel. The remaining
  // fields in this interface are discovery/VSM workflow state.
  chat: ChatController;
  // State
  sessionId: string | null;
  mode: ConversationMode;
  sessionLoading: boolean;
  sessionError: string | null;
  conversationHistory: ConversationMessage[];
  memorySummary: string;
  discoveryFeatures: DiscoveryFeaturesPayload;
  dsnModel: import('../utils/summaryParser').DsnModel;
  isLoading: boolean;
  inputBusy: boolean;
  isExtractingFeatures: boolean;
  newModel: NewModel;
  callStatus: string;
  // ── Narrate → Refine (002-narrate-capture) ─────────────────────
  // `phase` undefined ⇒ pre-feature session, rendered as refine
  // (FR-015). `spine` is the plain-language projection shown while
  // narrating; always an array (empty ⇒ calm narrate-empty state).
  phase: SessionPhase | undefined;
  spine: SpineStep[];
  // ── Agent OFFER tracking (002-narrate-capture, FR-008) ─────────
  // `turnKind` is the latest done-frame classification (authoritative,
  // always present). `transitionOffered` is derived: the agent is
  // offering the move to the map iff we are in narrate, the last turn
  // was an 'offer', and the SME hasn't dismissed the current offer.
  // `transitionError` surfaces a failed flip so the SME is never
  // silently stuck (P1-review nit); null when there is no error.
  turnKind?: 'question' | 'nudge' | 'offer';
  transitionOffered: boolean;
  transitionError: string | null;
  // ── Activity feedback (056; owner-agreed activity feedback, 2026-07-09) ──
  // The in-flight turn's background activities (advisory tool runs surfaced
  // by the `activity` SSE frame), for the shared SessionCapturePanel to
  // render. Optional and additive: discovery emits none today, so screens
  // without activity render exactly as before; the capability is inherited
  // the moment the server emits.
  activities?: ChatActivity[];

  // Actions
  createSession: (mode: ConversationMode, refs?: CreateSessionRefs) => Promise<string>;
  // Manual flip narrate → refine: POSTs the phase endpoint and, on
  // success, sets local phase to 'refine' (FR-011/FR-012).
  acceptTransition: () => Promise<void>;
  // Dismiss the CURRENT agent offer without changing phase — the SME
  // keeps narrating. Per-offer: a later done:offer re-offers (§6.2).
  dismissOffer: () => void;
  // Apply a chat2 `done` frame's per-turn fields (phase/spine/turnKind +
  // stripped newModel + per-offer dismissal reset). The single shared
  // writer used by BOTH the text path and the voice path so a frame is
  // applied identically in either modality (003-narrate-voice-feedback,
  // FR-001..FR-004, FR-003 reuse).
  applyTurnResult: (doneModel: DoneModel) => void;
  handleTextMessage: (message: string, files?: File[]) => Promise<void>;
  startVoiceCall: () => Promise<void>;
  endVoiceCall: () => void;
  downloadTranscript: () => void;
  restartSession: () => Promise<void>;
  setShowMemoryModal: (show: boolean) => void;
  showMemoryModal: boolean;
  setBacklogPanelOpen: (open: boolean) => void;
  backlogPanelOpen: boolean;
  refreshDiscoveryFeatures: () => Promise<void>;
  triggerDiscoveryFeatureExtraction: () => Promise<void>;
  importFromDocument: (
    documentText: string,
    opts?: { filename?: string; force?: boolean },
  ) => Promise<DocImportResult>;
  isImportingDocument: boolean;
  importFromMermaid: (
    args: { mermaid?: string; flowText?: string; force?: boolean },
  ) => Promise<DocImportResult>;
  isImportingMermaid: boolean;
  setFlowText: (
    args: { flowText: string; force?: boolean },
  ) => Promise<DocImportResult>;
  isSavingFlowText: boolean;
  // VSM direct edits — backs the inline metric editor in VsmCanvas and
  // the "Add to VSM" affordance on Flow cards. Optimistically updates
  // newModel.vsm in local state and then writes through.
  saveVsm: (vsm: import('../utils/flowModel').Vsm) => Promise<{ ok: boolean; error?: string }>;
}

// Result of a structured-document import. Same conflict shape as the
// repo flavour so the modal can reuse the confirm-dialog code path.
export interface DocImportResult {
  ok: boolean;
  status?: number;
  error?: string;
  conflict?: { existingStatementCount: number };
  payload?: {
    statementCount: number;
    rowCount: number;
    plannedRowCount: number;
    droppedRowCount: number;
    durationMs: number;
  };
}
